/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * @file AssignmentModalStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const panelScrollStyles = CommonStyles().scrollContainer({
  scrollbarWidth: 8,
  scrollbarColor: `${mixins.quaternary} transparent`,
  overflowY: 'auto'
});

const questionTopicsModalStyles = () => ({
  dialogPaper: {
    width: 880,
    maxWidth: 'calc(100% - 32px)',
    flexShrink: 0,
    height: 'calc(100vh - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 16,
    padding: 24,
    borderRadius: 24,
    boxSizing: 'border-box',
    boxShadow: '0px 1px 3px rgba(13, 0, 77, 0.05), 0px 1px 1px rgba(13, 0, 77, 0.1), 0px 2px 1px rgba(13, 0, 77, 0.15)',
    overflow: 'hidden',
    isolation: 'isolate',
    display: 'flex',
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      padding: 32
    },

    [mixins['mlg-min']]: {
      padding: 48
    },

    '& *': {
      boxSizing: 'border-box'
    },

    '&.MuiDialog-paper': {
      width: 880,
      maxWidth: 'calc(100% - 32px)'
    },

    '&.MuiDialog-paperScrollPaper': {
      overflow: 'hidden'
    }
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 2,
    padding: 8
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    minHeight: 0,
    flex: 1,
    overflow: 'hidden',

    [mixins['md-sm-min']]: {
      gap: 16
    },

    [mixins['mlg-min']]: {
      gap: 24
    }
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0
  },
  assignmentTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0,
    paddingRight: 32,
    wordBreak: 'break-word',

    [mixins['md-sm-min']]: {
      fontSize: mixins.fontSize24,
      lineHeight: '32px',
      letterSpacing: '-0.48px'
    }
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  metaChip: {
    border: '1px solid rgba(13, 0, 77, 0.45)',
    borderRadius: 4,
    padding: '4px 8px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    color: mixins.color_text_primary,
    backgroundColor: mixins.chalkWhiteFont
  },
  metaChipValue: {
    fontWeight: mixins.fontWeight600
  },
  sectionSelectHeaderLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0,
    paddingRight: 32,
    wordBreak: 'break-word',

    [mixins['md-sm-min']]: {
      fontSize: mixins.fontSize24,
      lineHeight: '32px',
      letterSpacing: '-0.48px'
    }
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  questionsPane: {
    backgroundColor: '#F6F6FB',
    minWidth: 0,
    minHeight: 0,
    flex: 1,
    padding: 16,
    overflowX: 'hidden',
    ...panelScrollStyles,
    borderRadius: 16
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  emptyQuestions: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: mixins.greyScaleColor,
    margin: 0
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    flexShrink: 0
  },
  doneButton: {
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    boxSizing: 'content-box',
    minWidth: 104,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&.MuiButton-text, &.MuiButton-textPrimary': {
      backgroundColor: mixins.amethyst,
      background: mixins.amethyst,
      color: mixins.chalkWhiteFont
    },

    '&:hover, &.MuiButton-text:hover, &.MuiButton-textPrimary:hover': {
      backgroundColor: mixins.amethystColorV2,
      background: mixins.amethystColorV2,
      color: mixins.chalkWhiteFont
    }
  },
  questionCard: {
    backgroundColor: mixins.chalkWhiteFont,
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 12,
    overflow: 'hidden',
    width: '100%'
  },
  questionCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 48,
    padding: '8px 12px',
    cursor: 'pointer',
    width: '100%',
    border: 'none',
    borderBottom: '1px solid rgba(13, 0, 77, 0.15)',
    backgroundColor: 'transparent',
    textAlign: 'left',
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: -2
    }
  },
  questionCardTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    color: mixins.color_text_primary,
    margin: 0,
    flex: 1
  },
  questionCardBody: {
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  questionStem: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    margin: 0,
    wordBreak: 'break-word'
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 48,
    padding: 12,
    borderRadius: 8,
    border: '1px solid rgba(13, 0, 77, 0.15)'
  },
  optionRowCorrect: {
    backgroundColor: '#EBF9F0',
    borderColor: '#00B057'
  },
  optionPrefix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    minHeight: 24,
    borderRadius: 1000,
    backgroundColor: '#E8E6F3',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    color: mixins.color_text_primary,
    flexShrink: 0
  },
  optionPrefixCorrect: {
    backgroundColor: '#C4EED2',
    color: '#006632'
  },
  optionLabel: {
    flex: 1,
    minWidth: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    color: mixins.color_text_primary,
    wordBreak: 'break-word'
  },
  optionLabelCorrect: {
    color: '#006632'
  },
  expandIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
    '& svg': {
      width: 12,
      height: 7,
      flexShrink: 0
    },
    '& svg path': {
      fill: mixins.navyColor
    }
  },
  expandIconExpanded: {
    transform: 'scaleY(-1)'
  },
  correctIcon: {
    display: 'inline-flex',
    flexShrink: 0,
    '& svg': {
      width: 18,
      height: 18
    }
  }
});

export const classes = createScopedClasses('aet-question-topics-modal', questionTopicsModalStyles());

export const scopedStyles = createScopedStyles(questionTopicsModalStyles(), classes);

/**
 * The dialog surface renders in a portal, so the scope root is the dialog
 * itself rather than a wrapper element inside the component tree.
 */
export const QuestionTopicsModalStylesRoot = styled(Dialog)(scopedStyles);

/**
 * The question card shares this rule map with the modal. It scopes itself so
 * the card renders correctly outside the dialog subtree as well.
 */
export const AssignmentQuestionCardStylesRoot = styled('article')(scopedStyles);

export const QuestionTopicsDoneButton = styled(Button)({
  '&&': questionTopicsModalStyles().doneButton
});

export default questionTopicsModalStyles;
